import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./booksHomePage.css";
import BaseNavbar from "../../commanComponents/navbar/baseNavbar";
import Footer from "../../commanComponents/footer/footer";
import { BookListService } from "../../../modules/services";
import { ROUTES } from "../../../constant";
import { clearData } from "../../../utils/localStorage";
import SkeletonLoading from "../../commanComponents/skeletonLoading/skeletonLoading";

const DEFAULT_PHYSICAL_SHIPPING = 90;

const FIXED_CATEGORIES = [
  { key: "ROBOTICS", name: "Robotics", icon: "R", count: 0 },
  { key: "ELECTRONICS", name: "Electronics", icon: "E", count: 0 },
  { key: "3D_PRINTING", name: "3D Printing", icon: "3D", count: 0 },
  { key: "AEROMODELING", name: "Aeromodelling", icon: "A", count: 0 },
  { key: "AI_ML", name: "AI & ML", icon: "AI", count: 0 },
  { key: "AUTOMOBILES", name: "Automobiles", icon: "AU", count: 0 },
];

const formatPrice = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const getSubjectDisplayName = (subject) => {
  const map = {
    ROBOTICS: "Robotics",
    ELECTRONICS: "Electronics",
    "3D_PRINTING": "3D Printing",
    AEROMODELING: "Aeromodelling",
    AI_ML: "AI & ML",
    AUTOMOBILES: "Automobiles",
  };
  return map[subject] || subject;
};

const getGradeDisplay = (gradeLevels) => {
  if (!gradeLevels || gradeLevels.length === 0) return "N/A";
  if (gradeLevels.length === 1) return `Grade ${gradeLevels[0].name}`;
  return `Grades ${gradeLevels[0].name}-${gradeLevels[gradeLevels.length - 1].name}`;
};

const getPhysicalBundlePrice = (book) =>
  Number(book?.price || 0) + DEFAULT_PHYSICAL_SHIPPING;

const BooksHomePage = () => {
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState(FIXED_CATEGORIES);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    message: "",
    visible: false,
  });
  const [selectedSubject, setSelectedSubject] = useState("all");

  const navigate = useNavigate();

  useEffect(() => {
    setCategories(FIXED_CATEGORIES);
    (async () => {
      setLoading(true);
      const responseData = await BookListService({});

      if (responseData === "error" || !responseData.data?.[0]?.results) {
        clearData();
        navigate(ROUTES.LOGIN);
        return;
      }

      if (responseData.status_code !== 200 && responseData.status_code !== 201) {
        setLoading(false);
        return;
      }

      const results = responseData.data[0].results;
      setBooks(results);
      setFilteredBooks(results);

      const countMap = {};
      results.forEach((book) => {
        const subject = book.subject?.toUpperCase();
        if (subject && FIXED_CATEGORIES.some((category) => category.key === subject)) {
          countMap[subject] = (countMap[subject] || 0) + 1;
        }
      });

      setCategories(
        FIXED_CATEGORIES.map((category) => ({
          ...category,
          count: countMap[category.key] || 0,
        }))
      );
      setLoading(false);
    })();
  }, [navigate]);

  const showNotification = (message) => {
    setNotification({ message, visible: true });
    window.setTimeout(() => {
      setNotification({ message: "", visible: false });
    }, 3000);
  };

  const filterBooks = (subject) => {
    setSelectedSubject(subject);
    if (subject === "all") {
      setFilteredBooks(books);
    } else {
      setFilteredBooks(books.filter((book) => book.subject === subject));
    }

    showNotification(
      `Showing ${subject === "all" ? "all" : getSubjectDisplayName(subject)} books`
    );
  };

  useEffect(() => {
    const checkVisibility = () => {
      document.querySelectorAll(".books-home-fade-in").forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          element.classList.add("visible");
        }
      });
    };

    window.addEventListener("scroll", checkVisibility);
    window.addEventListener("load", checkVisibility);
    return () => {
      window.removeEventListener("scroll", checkVisibility);
      window.removeEventListener("load", checkVisibility);
    };
  }, []);

  return (
    <>
      <BaseNavbar activeItem="BOOKS" />

      <section className="books-home-hero" id="home">
        <div className="books-home-hero-container">
          <div className="books-home-hero-content">
            <h1>
              Discover the <span className="books-home-hero-highlight">Perfect Books</span>{" "}
              for Every Student
            </h1>
            <p className="books-home-hero-subtitle">
              Every title is available as an instant E-Book or as a Physical + E-Book
              bundle for learners who want both delivery and digital access.
            </p>
            <div className="books-home-hero-cta">
              <a href="#featured" className="books-home-btn books-home-btn-primary">
                Explore Books
              </a>
              <a href="#categories" className="books-home-btn books-home-btn-secondary">
                Browse Categories
              </a>
            </div>
            <div className="books-home-hero-stats">
              <div className="books-home-stat-item">
                <span className="books-home-stat-number">{books.length}+</span>
                <span className="books-home-stat-label">Educational Books</span>
              </div>
              <div className="books-home-stat-item">
                <span className="books-home-stat-number">2</span>
                <span className="books-home-stat-label">Purchase Formats</span>
              </div>
              <div className="books-home-stat-item">
                <span className="books-home-stat-number">15K+</span>
                <span className="books-home-stat-label">Happy Students</span>
              </div>
            </div>
          </div>
          <div className="books-home-hero-image">
            <div className="books-home-hero-book-stack"></div>
            <div className="books-home-floating-elements">
              <div className="books-home-floating-book"></div>
              <div className="books-home-floating-book"></div>
              <div className="books-home-floating-book"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="books-home-categories books-home-fade-in" id="categories">
        <div className="books-home-container">
          <div className="books-home-section-header">
            <h2 className="books-home-section-title">Explore by Subjects</h2>
            <p className="books-home-section-subtitle">
              Build your library by subject and choose the format that fits your study style.
            </p>
          </div>

          <div className="books-home-categories-grid">
            <div
              className={`books-home-category-card ${selectedSubject === "all" ? "highlighted" : ""}`}
              onClick={() => filterBooks("all")}
            >
              <div className="books-home-category-icon">All</div>
              <h3>All Subjects</h3>
              <p>Browse all available books</p>
              <span className="books-home-book-count">{books.length} Books</span>
            </div>

            {categories.map((category) => (
              <div
                key={category.key}
                className={`books-home-category-card ${selectedSubject === category.key ? "highlighted" : ""}`}
                onClick={() => filterBooks(category.key)}
              >
                <div className="books-home-category-icon">{category.icon}</div>
                <h3>{category.name}</h3>
                <p>Hands-on learning in {category.name.toLowerCase()}</p>
                <span className="books-home-book-count">{category.count} Books</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="books-home-filters">
        <div className="books-home-filters-container">
          <div className="books-home-filter-group">
            <label>Subject:</label>
            <select value={selectedSubject} onChange={(event) => filterBooks(event.target.value)}>
              <option value="all">All Subjects</option>
              {categories.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {loading ? (
        <section className="books-home-featured-books books-home-fade-in">
          <div className="books-home-container">
            <div className="books-home-section-header">
              <h2 className="books-home-section-title">Loading Books...</h2>
            </div>
            <div className="books-home-books-grid">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="books-home-book-card"
                  style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                  <SkeletonLoading customStyle={{ height: "200px", width: "100%" }} />
                  <div style={{ padding: "1.5rem" }}>
                    <SkeletonLoading
                      customStyle={{ height: "20px", width: "80%", marginBottom: "12px" }}
                    />
                    <SkeletonLoading
                      customStyle={{ height: "16px", width: "60%", marginBottom: "16px" }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                      }}
                    >
                      <SkeletonLoading customStyle={{ height: "24px", width: "70px" }} />
                      <SkeletonLoading customStyle={{ height: "20px", width: "50px" }} />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <SkeletonLoading customStyle={{ height: "32px", width: "90px" }} />
                      <SkeletonLoading
                        customStyle={{ height: "40px", width: "120px", borderRadius: "20px" }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="books-home-featured-books books-home-fade-in" id="featured">
          <div className="books-home-container">
            <div className="books-home-section-header">
              <h2 className="books-home-section-title">
                {selectedSubject === "all"
                  ? "All Educational Books"
                  : `${getSubjectDisplayName(selectedSubject)} Books`}
              </h2>
              <p className="books-home-section-subtitle">
                {filteredBooks.length} book{filteredBooks.length !== 1 ? "s" : ""} available
              </p>
            </div>

            <div className="books-home-books-grid">
              {filteredBooks.length === 0 ? (
                <p>No books found in this category.</p>
              ) : (
                filteredBooks.map((book) => (
                  <div
                    key={book.id}
                    className="modern-book-card"
                    onClick={() => navigate(`${ROUTES.BOOKS_SELLING_DETAILS_PAGE}/${book.slug}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="modern-book-image-wrapper">
                      <img
                        src={book.cover_image_url}
                        alt={book.title}
                        className="modern-book-cover"
                      />

                      <div className="modern-book-badges">
                        {book.is_featured && <span className="badge-featured">Featured</span>}
                        {book.has_discount && <span className="badge-sale">Sale</span>}
                      </div>

                      <div className="modern-quick-actions">
                        <button
                          className="quick-add-btn"
                          onClick={() => navigate(`${ROUTES.BOOKS_SELLING_DETAILS_PAGE}/${book.slug}`)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>

                    <div className="modern-book-info">
                      <h3 className="modern-book-title">{book.title}</h3>
                      <p className="modern-book-subtitle">{book.subtitle}</p>

                      <div className="modern-book-meta">
                        <span className="grade-tag">{getGradeDisplay(book.grade_level)}</span>
                        <span className="rating">★ {book.average_rating || "New"}</span>
                      </div>

                      <div className="modern-book-format-row">
                        <span className="modern-format-pill is-digital">E-Book</span>
                        <span className="modern-format-pill is-physical">Physical + E-Book</span>
                      </div>

                      <div className="modern-book-purchase-preview">
                        <div className="modern-book-price-band">
                          <span className="modern-book-price-label">From</span>
                          <strong>{formatPrice(book.price)}</strong>
                        </div>
                        <div className="modern-book-price-note">
                          Physical bundle {formatPrice(getPhysicalBundlePrice(book))}
                          <span> with shipping</span>
                        </div>
                      </div>

                      <div className="modern-book-price-actions">
                        <div className="price-group">
                          <span className="current-price">{formatPrice(book.price)}</span>
                          {book.has_discount && (
                            <span className="original-price">
                              {formatPrice(book.original_price)}
                            </span>
                          )}
                        </div>
                        <span className="modern-book-format-count">2 formats</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      <section className="books-home-newsletter books-home-fade-in">
        <div className="books-home-container">
          <div className="books-home-newsletter-content">
            <h3>Stay Updated with New Educational Resources</h3>
            <p>
              Get notified about new book releases, educational tips, and exclusive
              offers.
            </p>
            <div className="books-home-newsletter-form">
              <input
                type="email"
                className="books-home-newsletter-input"
                placeholder="Enter your email"
              />
              <button className="books-home-newsletter-btn">Subscribe</button>
            </div>
          </div>
        </div>
      </section>

      {notification.visible && (
        <div className="books-home-notification">{notification.message}</div>
      )}

      <Footer />
    </>
  );
};

export default BooksHomePage;
